import React, { useState } from 'react';
import './PowerPlay.scss';
import { Helmet } from 'react-helmet';
import { useInView } from '../hooks/useInView';

import heroBg from '../Assets/Images/powerplay/pp-hero-bg.svg';
import heroBgMobile from '../Assets/Images/powerplay/pp-hero-bg-mobile.svg';
import heroLogo from '../Assets/Images/powerplay/pp-hero-logo.svg';
import iconProcess from '../Assets/Images/powerplay/pp-icon-process.svg';
import iconApps from '../Assets/Images/powerplay/pp-icon-apps.svg';
import iconWorkflow from '../Assets/Images/powerplay/pp-icon-workflow.svg';
import iconAnalytics from '../Assets/Images/powerplay/Group 1752.svg';
import iconCustom from '../Assets/Images/powerplay/pp-icon-custom.svg';
import checkOrange from '../Assets/Images/powerplay/pp-check-orange.svg';
import checkDark from '../Assets/Images/powerplay/pp-check-dark.svg';
import keyBg from '../Assets/Images/powerplay/pp-key-bg.svg';
import keyBgDesktop from '../Assets/Images/powerplay/pp-key-bg-desktop.png';

const serviceCards = [
  {
    icon: iconProcess,
    title: 'Process\nAutomation',
    body: 'Replace manual, repetitive tasks with automated flows — including RPA for legacy system integration.',
  },
  {
    icon: iconApps,
    title: 'Business\nApplications',
    body: 'Model-driven and canvas apps tailored to your exact process — procurement, HR, operations, finance.',
  },
  {
    icon: iconWorkflow,
    title: 'Workflow\nOrchestration',
    body: 'Structured approval chains, SLA tracking and owner assignment - eliminating the inbox black hole.',
  },
];

const serviceCardsRow2 = [
  {
    icon: iconAnalytics,
    title: 'Analytics\n& Reporting',
    body: 'Native Power BI integration for real-time visibility into process performance and business KPIs.',
  },
  {
    icon: iconCustom,
    title: 'Custom Professional\nDevelopment',
    body: "When low-code isn't enough - fully custom applications built for scale, security, and maintainability.",
  },
];

const businessOutcomes = [
  'Automation and full transparency of business processes',
  'Elimination of manual administrative work and resulting errors',
  'Empowerment of business users — enabling action instead of waiting',
  'Excel-based processes transitioned to structured, governed solutions',
];

const techOutcomes = [
  'Rapid development without complex coding or IT bottlenecks',
  'Integration with existing systems via connectors or RPA',
  'Enterprise-level security and compliance',
  'Native analytics with direct access to data sources',
];

const allServiceCards = [...serviceCards, ...serviceCardsRow2];

function PowerPlay() {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const { ref: heroRef, visible: heroVisible } = useInView(0.05);
  const { ref: introRef, visible: introVisible } = useInView(0.1);
  const { ref: howRef, visible: howVisible } = useInView(0.1);
  const { ref: keyRef, visible: keyVisible } = useInView(0.1);
  const { ref: outcomesRef, visible: outcomesVisible } = useInView(0.1);

  return (
    <div className="pp-page">
      <Helmet>
        <title>Artifit - Power Play</title>
        <meta
          name="description"
          content="From spreadsheets and manual workflows to structured, automated solutions - fast."
        />
      </Helmet>

      {/* ── HERO ── */}
      <section className="pp-hero">
        <img src={heroBg} alt="" className="pp-hero-bg pp-hero-bg--desktop" />
        <img src={heroBgMobile} alt="" className="pp-hero-bg pp-hero-bg--mobile" />
        <div
          className={`pp-hero-content pp-anim-fade-up${heroVisible ? ' is-visible' : ''}`}
          ref={heroRef as React.RefObject<HTMLDivElement>}
        >
          <img src={heroLogo} alt="" className="pp-hero-logo" />
          <h1 className="pp-hero-title">
            <span className="pp-orange">Power</span>{' '}Play
          </h1>
          <p className="pp-hero-sub">
            From spreadsheets and
            <br />
            manual workflows to <span className="pp-hero-sub-accent">structured,</span>
            <br />
            <span className="pp-hero-sub-accent">automated solutions - fast.</span>
          </p>
          <a href="mailto:office@artifit.rs" className="pp-btn-primary">
            Get in touch
          </a>
        </div>
      </section>

      {/* ── GRADIENT WRAPPER: intro + how it works ── */}
      <div className="pp-mid-wrapper">
        {/* ── INTRO TEXT ── */}
        <section className="pp-intro">
          <div
            className={`pp-intro-inner pp-anim-fade-up${introVisible ? ' is-visible' : ''}`}
            ref={introRef as React.RefObject<HTMLDivElement>}
          >
            <p className="pp-intro-text">
              There&apos;s a category of work that falls through the crack{' '}
              <strong>
                between what your systems can do and what your business needs.
              </strong>
            </p>
            <p className="pp-intro-text">
              Processes that live in Excel. Approval chains that happen over email. Tracking that
              depends on one person&apos;s memory.
            </p>
            <p className="pp-intro-cta">
              This is exactly the space{' '}
              <span className="pp-orange">Power Play</span>
              {' '}is designed for.
            </p>
            <div className="pp-intro-divider" />
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="pp-how" id="pp-how">
          <div
            className={`pp-how-inner pp-anim-fade-up${howVisible ? ' is-visible' : ''}`}
            ref={howRef as React.RefObject<HTMLDivElement>}
          >
            <h2 className="pp-how-title">
              <span className="pp-orange">How it</span>{' '}works
            </h2>
            <p className="pp-how-desc">
              We turn those fragmented workflows into{' '}
              <strong>structured, automated, governed applications</strong>
              {' '}— in weeks, not months. Business teams stay in control. IT gets visibility
              and security without the bottlenecks.
            </p>

            {/* Desktop grid */}
            <div className="pp-how-desktop">
              <div className="pp-how-cards pp-how-cards--row1">
                {serviceCards.map((card, i) => (
                  <div key={i} className="pp-how-card">
                    <img src={card.icon} alt="" className="pp-how-card-icon" />
                    <h3 className="pp-how-card-title">{card.title}</h3>
                    <p className="pp-how-card-body">{card.body}</p>
                  </div>
                ))}
              </div>
              <div className="pp-how-cards pp-how-cards--row2">
                {serviceCardsRow2.map((card, i) => (
                  <div key={i} className="pp-how-card">
                    <img src={card.icon} alt="" className="pp-how-card-icon" />
                    <h3 className="pp-how-card-title">{card.title}</h3>
                    <p className="pp-how-card-body">{card.body}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile accordion */}
            <div className="pp-how-mobile">
              {allServiceCards.map((card, i) => (
                <div
                  key={i}
                  className={`pp-how-item${expandedCard === i ? ' pp-how-item--open' : ''}`}
                  onClick={() => setExpandedCard(expandedCard === i ? null : i)}
                >
                  <div className="pp-how-item-row">
                    <img src={card.icon} alt="" className="pp-how-item-icon" />
                    <span className="pp-how-item-title">{card.title.replace('\n', ' ')}</span>
                    <svg className="pp-how-item-chevron" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M4 7l5 5 5-5" stroke="#ef4223" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <p className="pp-how-item-desc">{card.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ── KEY POINTS + OUTCOMES wrapper (mobile bg spans both) ── */}
      <div className="pp-key-outcomes-wrapper">
        <img src={keyBg} alt="" className="pp-key-mobile-bg" />

      {/* ── KEY POINTS TO CONSIDER ── */}
      <section className="pp-key">
        <img src={keyBgDesktop} alt="" className="pp-key-desktop-bg" />
        <div
          className={`pp-key-inner pp-anim-fade-up${keyVisible ? ' is-visible' : ''}`}
          ref={keyRef as React.RefObject<HTMLDivElement>}
        >
          <h2 className="pp-key-title">
            <span className="pp-orange">Key</span>{' '}points to consider
          </h2>
          <ul className="pp-key-list">
            <li>How much of your processes still depends on Excel files and disconnected systems?</li>
            <li>How long does it take your teams to get the tools they need?</li>
            <li>
              How many hours do your people spend moving data between systems that should talk
              to each other?
            </li>
          </ul>
        </div>
      </section>

      {/* ── OUTCOMES ── */}
      <section className="pp-outcomes">
        <div
          className={`pp-outcomes-inner pp-anim-fade-up${outcomesVisible ? ' is-visible' : ''}`}
          ref={outcomesRef as React.RefObject<HTMLDivElement>}
        >
          <div className="pp-outcomes-card">
            <div className="pp-outcomes-header pp-outcomes-header--orange">Business Outcomes</div>
            <ul className="pp-outcomes-list">
              {businessOutcomes.map((item, i) => (
                <li key={i} className="pp-outcomes-item">
                  <img src={checkOrange} alt="" className="pp-outcomes-check" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="pp-outcomes-card">
            <div className="pp-outcomes-header pp-outcomes-header--dark">Technology Outcomes</div>
            <ul className="pp-outcomes-list">
              {techOutcomes.map((item, i) => (
                <li key={i} className="pp-outcomes-item">
                  <img src={checkDark} alt="" className="pp-outcomes-check" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      </div>{/* end pp-key-outcomes-wrapper */}

      {/* ── CTA ── */}
      <section className="pp-cta">
        <p className="pp-cta-text">
          Every process deserves a proper setup. Let&apos;s build yours.
        </p>
        <a href="mailto:office@artifit.rs">
          <button className="pp-cta-btn">Get in touch</button>
        </a>
      </section>
    </div>
  );
}

export default PowerPlay;
